import { useParams } from 'react-router-dom';
import { PrivyProvider, useConnectWallet, usePrivy, useWallets } from '@privy-io/react-auth';
import { Button, Card, Flex, Layout, Skeleton, Typography, App } from 'antd';
import { useConfirmWalletVerification, useWalletVerificationSession } from './useWalletVerificationSession';
import { FingerPrintIcon } from '@heroicons/react/24/outline';
import { css } from '@emotion/css';
import { useEffect, useState } from 'react';

export const WalletVerificationPage = () => {
  const appStyles = css`
    height: 100%;
  `;
  return (
    <PrivyProvider
      appId="cma6nexze027ml50l13nhp39k"
      clientId="client-WY6L7B7DLQCeGeiXpQ4bAYQvVqFFFuyizNNVmHWcGgyPs"
      config={{
        appearance: {
          walletChainType: 'ethereum-and-solana',
          walletList: ['detected_wallets', 'metamask', 'coinbase_wallet', 'rainbow', 'wallet_connect'],
        },
      }}
    >
      <App className={appStyles}>
        <WalletVerificationApp />
      </App>
    </PrivyProvider>
  );
};

export const WalletVerificationApp = () => {
  const { verificationHash } = useParams();
  const [isSuccess, setIsSuccess] = useState(false);
  const [isSigning, setIsSigning] = useState(false);
  const walletVerificationSession = useWalletVerificationSession(verificationHash);
  const { confirmWalletVerification } = useConfirmWalletVerification();
  const { ready } = usePrivy();
  const { wallets } = useWallets();
  const { connectWallet } = useConnectWallet();
  const { message } = App.useApp();

  const layoutStyles = css`
    height: 100%;
  `;

  const contentStyles = css`
    padding: 16px;
  `;

  const headerStyles = css`
    margin-top: 0;
  `;

  const cardStyles = css`
    max-width: 460px;
  `;

  const handleSignMessage = async () => {
    try {
      setIsSigning(true);
      const wallet = wallets[0];
      const provider = await wallet.getEthereumProvider();
      const address = wallet.address;
      const signature = await provider.request({
        method: 'personal_sign',
        params: [walletVerificationSession.data?.data?.challengeMessage, address],
      });
      console.log(signature);
      await confirmWalletVerification({
        signature,
        hash: verificationHash!,
        sessionId: walletVerificationSession.data?.data?.sessionId,
      });
      setIsSuccess(true);
      message.success('Wallet ownership verified successfully');
    } catch (err) {
      console.log(err);
      message.error('Failed to sign verification message');
    } finally {
      setIsSigning(false);
    }
  };

  useEffect(() => {
    if (walletVerificationSession.data?.data?.verificationStatus === 'verified') {
      setIsSuccess(true);
    }
  }, [walletVerificationSession.data]);

  const renderLayoutContent = () => {
    if (!ready || walletVerificationSession.isPending) {
      return <Skeleton active />;
    }
    return (
      <Flex justify="center">
        <Card className={cardStyles}>
          {isSuccess ? (
            <Flex vertical={true} gap={'middle'}>
              <Typography.Title className={headerStyles} level={3}>
                Verification Successful! 🎉
              </Typography.Title>
              <Typography.Text>Your wallet ownership has been verified successfully ✅</Typography.Text>
              <Typography.Text type="success">
                You can now close this window and continue with your transactions 🚀
              </Typography.Text>
            </Flex>
          ) : (
            <Flex vertical={true} gap={'middle'}>
              <Typography.Title className={headerStyles} level={3}>
                Verify Wallet Ownership
              </Typography.Title>
              <Typography.Text>
                We need to verify that you own this wallet to continue. Please connect your wallet and sign the
                verification message.
              </Typography.Text>
              <Typography.Text strong>Wallet Address To Verify:</Typography.Text>
              <Typography.Text code>{walletVerificationSession.data?.data?.walletAddress}</Typography.Text>

              {wallets.length ? (
                <Button
                  loading={isSigning}
                  disabled={isSigning}
                  icon={<FingerPrintIcon width={15} />}
                  type="primary"
                  onClick={handleSignMessage}
                >
                  Sign Verification Message
                </Button>
              ) : (
                <Button
                  type="primary"
                  onClick={() => {
                    connectWallet();
                  }}
                >
                  Connect Your Wallet
                </Button>
              )}
            </Flex>
          )}
        </Card>
      </Flex>
    );
  };

  return (
    <Layout className={layoutStyles}>
      <Layout.Content className={contentStyles}>{renderLayoutContent()}</Layout.Content>
    </Layout>
  );
};
